const GA4_ID = 'G-892MCX6JE3';
const MILESTONES = [25, 50, 75];
const MIN_WATCH_MS = 1000;

const ENTRIES = [
  ['badge_popup', '.video-popup-container'],
  ['gallery_slide', '.product__media-list'],
];

const script = document.querySelector('script[data-video-tracking]');
const PRODUCT_TITLE = script?.dataset.productTitle ?? document.title;
const PRODUCT_ID = script?.dataset.productId ?? '';
const DEBUG = script?.dataset.debug === 'true';

function send(name, params, { beacon = false } = {}) {
  if (typeof window.gtag !== 'function') return;
  const payload = {
    ...params,
    send_to: GA4_ID,
    ...(DEBUG && { debug_mode: true }),
    ...(beacon && { transport_type: 'beacon' }),
  };
  window.gtag('event', name, payload);
  if (DEBUG) console.log('[Video]', name, payload);
}

function entryOf(video) {
  return ENTRIES.find(([, selector]) => video.closest(selector))?.[0] ?? null;
}

function videoUrl(video) {
  return (video.currentSrc || video.src || '').slice(0, 100);
}

class VideoTracker {
  static #byVideo = new Map();

  static attach(video) {
    if (VideoTracker.#byVideo.has(video)) return;
    const entry = entryOf(video);
    if (!entry) return;
    VideoTracker.#byVideo.set(video, new VideoTracker(video, entry));
  }

  static of(video) {
    return VideoTracker.#byVideo.get(video);
  }

  static stopAll(reason) {
    for (const tracker of VideoTracker.#byVideo.values()) tracker.stop(reason, { beacon: true });
  }

  static resumeAll() {
    for (const tracker of VideoTracker.#byVideo.values()) tracker.resume();
  }

  #video;
  #entry;
  #started = false;
  #completed = false;
  #settled = false;
  #segmentMs = 0;
  #playStart = null;
  #milestones = new Set();
  #visible = false;

  constructor(video, entry) {
    this.#video = video;
    this.#entry = entry;

    new IntersectionObserver(
      ([e]) => { this.#visible = e.isIntersecting; },
      { threshold: 0.5 },
    ).observe(video);

    video.addEventListener('play', () => this.#onPlay());
    video.addEventListener('pause', () => this.#pauseClock());
    video.addEventListener('timeupdate', () => this.#onTimeUpdate());
    video.addEventListener('ended', () => this.#onEnded());
    if (!video.paused) this.#onPlay();

    if (DEBUG) console.log('[Video] Tracker aktiv:', entry, videoUrl(video));
  }

  #percentAt(seconds) {
    const duration = this.#video.duration;
    return Number.isFinite(duration) && duration > 0
      ? Math.round((seconds / duration) * 100)
      : 0;
  }

  #params(extra = {}) {
    const video = this.#video;
    return {
      video_current_time: Math.round(video.currentTime || 0),
      video_duration: Math.round(video.duration || 0),
      video_percent: this.#percentAt(video.currentTime || 0),
      video_provider: 'shopify',
      video_title: PRODUCT_TITLE.slice(0, 100),
      video_url: videoUrl(video),
      visible: this.#visible,
      video_entry: this.#entry,
      product_id: PRODUCT_ID,
      ...extra,
    };
  }

  #pauseClock() {
    if (this.#playStart === null) return;
    this.#segmentMs += Date.now() - this.#playStart;
    this.#playStart = null;
  }

  #takeSeconds() {
    this.#pauseClock();
    const ms = this.#segmentMs;
    this.#segmentMs = 0;
    return ms;
  }

  #onPlay() {
    this.#settled = false;
    this.#playStart ??= Date.now();
    if (this.#started) return;
    this.#started = true;
    send('video_start', this.#params());
  }

  #onTimeUpdate() {
    const percent = this.#percentAt(this.#video.currentTime);
    for (const milestone of MILESTONES) {
      if (percent < milestone || this.#milestones.has(milestone)) continue;
      this.#milestones.add(milestone);
      send('video_progress', this.#params({ video_percent: milestone }));
    }
  }

  #onEnded() {
    const ms = this.#takeSeconds();
    this.#settled = true;

    if (!this.#completed) {
      this.#completed = true;
      send('video_complete', this.#params({
        video_percent: 100,
        video_watched_seconds: Math.round(ms / 1000),
      }));
    } else if (ms >= MIN_WATCH_MS) {
      send('video_stop', this.#params({
        stop_reason: 'ended',
        video_watched_seconds: Math.round(ms / 1000),
      }));
    }
  }

  stop(reason, { beacon = false } = {}) {
    if (this.#settled || !this.#started) {
      this.#pauseClock();
      return;
    }
    const ms = this.#takeSeconds();
    this.#settled = true;
    if (ms < MIN_WATCH_MS) return;

    send('video_stop', this.#params({
      stop_reason: reason,
      video_watched_seconds: Math.round(ms / 1000),
    }), { beacon });
  }

  resume() {
    if (this.#video.paused || this.#video.ended) return;
    this.#settled = false;
    this.#playStart ??= Date.now();
  }
}

if (DEBUG) {
  console.log('[Video] Tracking geladen fuer "' + PRODUCT_TITLE + '" — wartet auf Videos '
    + '(Galerie-Video abspielen oder Badge-Popup oeffnen).');
}

function videosIn(node) {
  if (node.nodeName === 'VIDEO') return [node];
  return node.querySelectorAll?.('video') ?? [];
}

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      for (const video of videosIn(node)) VideoTracker.attach(video);
    }
    for (const node of mutation.removedNodes) {
      for (const video of videosIn(node)) VideoTracker.of(video)?.stop('popup_closed');
    }
  }
}).observe(document.body, { childList: true, subtree: true });

for (const video of document.querySelectorAll('video')) VideoTracker.attach(video);

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') VideoTracker.stopAll('page_hidden');
  else VideoTracker.resumeAll();
});

window.addEventListener('pagehide', () => VideoTracker.stopAll('page_hide'));
